import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControlLabel,
  Switch,
  TextField,
  Divider
} from '@mui/material';
import { Sync, CloudSync, Refresh, CheckCircle, Error } from '@mui/icons-material';
import { authService } from '../services/authService';

interface Authority {
  id: string;
  code: string;
  name: string;
  owner_code?: string;
  type_name?: string;
  budget_profile_name?: string;
  asl_id?: string;
  ext_av_enabled: boolean;
  disconnected: boolean;
  last_sync_at?: string;
  created_at: string;
  updated_at: string;
}

interface SyncResult {
  success: boolean;
  authority?: Authority;
  wasCreated?: boolean;
  nsisCode?: string;
  total?: number;
  created?: number;
  updated?: number;
  errors?: number;
  errorDetails?: string[];
  message?: string;
}

const AuthoritySyncPage: React.FC = () => {
  const [authorities, setAuthorities] = useState<Authority[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [syncAll, setSyncAll] = useState(false);
  const [customNsisCode, setCustomNsisCode] = useState('');

  // Carica le authorities al mount
  useEffect(() => {
    loadAuthorities();
  }, []);

  const loadAuthorities = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/recup-sync/authorities', {
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw { message: `HTTP ${response.status}: ${response.statusText}` };
      }

      const data = await response.json();
      
      if (data.success) {
        setAuthorities(data.data || []);
      } else {
        throw { message: data.error || 'Errore nel caricamento authorities' };
      }
    } catch (err) {
      const errorMessage = err && typeof err === 'object' && 'message' in err ? (err as Error).message : 'Errore sconosciuto';
      setError(`Errore nel caricamento authorities: ${errorMessage}`);
      console.error('Errore nel caricamento authorities:', err);
    } finally {
      setLoading(false);
    }
  };

  const syncAuthorities = async () => {
    setSyncing(true);
    setError(null);
    setSuccess(null);
    setSyncResult(null);

    try {
      const response = await fetch('/api/recup-sync/sync/authorities', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          syncAll,
          nsisCode: customNsisCode || undefined
        })
      });

      if (!response.ok) {
        throw { message: `HTTP ${response.status}: ${response.statusText}` };
      }

      const data = await response.json();
      
      if (data.success) {
        setSyncResult(data);
        setSuccess('Sincronizzazione completata con successo!');
        
        // Ricarica la lista delle authorities
        await loadAuthorities();
      } else {
        throw { message: data.error || 'Errore nella sincronizzazione' };
      }
    } catch (err) {
      const errorMessage = err && typeof err === 'object' && 'message' in err ? (err as Error).message : 'Errore sconosciuto';
      setError(`Errore nella sincronizzazione: ${errorMessage}`);
      console.error('Errore nella sincronizzazione:', err);
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('it-IT');
  };

  const renderSyncResult = () => {
    if (!syncResult) return null;

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Risultato Sincronizzazione
          </Typography>
          
          {syncAll ? (
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary">Totale</Typography>
                <Typography variant="h6">{syncResult.total || 0}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary">Create</Typography>
                <Typography variant="h6" color="success.main">{syncResult.created || 0}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary">Aggiornate</Typography>
                <Typography variant="h6" color="info.main">{syncResult.updated || 0}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary">Errori</Typography>
                <Typography variant="h6" color="error.main">{syncResult.errors || 0}</Typography>
              </Grid>
            </Grid>
          ) : (
            <Box>
              <Typography variant="body1">
                <strong>NSIS Code:</strong> {syncResult.nsisCode}
              </Typography>
              {syncResult.authority && (
                <>
                  <Typography variant="body1">
                    <strong>Authority:</strong> {syncResult.authority.name} ({syncResult.authority.code})
                  </Typography>
                  <Typography variant="body1">
                    <strong>Status:</strong> {syncResult.wasCreated ? 'Creata' : 'Aggiornata'}
                  </Typography>
                </>
              )}
            </Box>
          )}

          {syncResult.errorDetails && syncResult.errorDetails.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="error">
                Dettagli Errori:
              </Typography>
              <ul>
                {syncResult.errorDetails.map((error, index) => (
                  <li key={index}>
                    <Typography variant="body2" color="error">
                      {error}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Sincronizzazione Authorities
      </Typography>

      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Gestione della sincronizzazione delle authorities dal sistema RECUP.
        Le authorities rappresentano gli enti che gestiscono le strutture sanitarie.
      </Typography>

      {/* Controlli di Sincronizzazione */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Controlli Sincronizzazione
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={syncAll}
                    onChange={(e) => setSyncAll(e.target.checked)}
                    disabled={syncing}
                  />
                }
                label="Sincronizza tutte le authorities"
              />
            </Grid>
            
            {!syncAll && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="Codice NSIS Custom (opzionale)"
                  value={customNsisCode}
                  onChange={(e) => setCustomNsisCode(e.target.value)}
                  disabled={syncing}
                  size="small"
                  fullWidth
                  helperText="Lascia vuoto per usare il codice di configurazione"
                />
              </Grid>
            )}
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={syncing ? <CircularProgress size={20} /> : <CloudSync />}
              onClick={syncAuthorities}
              disabled={syncing}
              sx={{ mr: 2 }}
            >
              {syncing 
                ? 'Sincronizzazione in corso...' 
                : syncAll 
                  ? 'Sincronizza Tutte' 
                  : 'Sincronizza per NSIS Code'
              }
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadAuthorities}
              disabled={loading || syncing}
            >
              Ricarica Lista
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Messaggi di Errore/Successo */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Risultato Sincronizzazione */}
      {renderSyncResult()}

      {/* Lista Authorities */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Authorities nel Database ({authorities.length})
          </Typography>
          
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : authorities.length === 0 ? (
            <Alert severity="info">
              Nessuna authority trovata nel database. Esegui la sincronizzazione per popolarlo.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Codice</TableCell>
                    <TableCell>Owner Code</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>ASL</TableCell>
                    <TableCell>Flags</TableCell>
                    <TableCell>Ultima Sync</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {authorities.map((authority) => (
                    <TableRow key={authority.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {authority.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={authority.code} size="small" />
                      </TableCell>
                      <TableCell>
                        {authority.owner_code && (
                          <Chip label={authority.owner_code} size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {authority.type_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {authority.asl_id || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          {authority.ext_av_enabled && (
                            <Chip label="Ext AV" size="small" color="primary" />
                          )}
                          {authority.disconnected && (
                            <Chip label="Disconnected" size="small" color="error" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(authority.last_sync_at)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AuthoritySyncPage;